type LogContext = Record<string, unknown>;
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const REDACTED_KEYS = /token|hand|cards?|state|sdp|offer|answer|secret|credential/i;
const isProduction = Boolean((import.meta as ImportMeta & { env?: { PROD?: boolean } }).env?.PROD);

export const logger = {
  debug(message: string, context?: LogContext): void {
    write('debug', message, context);
  },
  info(message: string, context?: LogContext): void {
    write('info', message, context);
  },
  warn(message: string, context?: LogContext): void {
    write('warn', message, context);
  },
  error(message: string, context?: LogContext): void {
    write('error', message, context);
  },
};

function write(level: LogLevel, message: string, context?: LogContext): void {
  if (isProduction && (level === 'debug' || level === 'info')) return;
  const safeContext = context ? sanitizeContext(context) : undefined;
  const output = safeContext && Object.keys(safeContext).length > 0 ? [message, safeContext] : [message];
  if (level === 'error') console.error(...output);
  else if (level === 'warn') console.warn(...output);
  else if (level === 'info') console.info(...output);
  else console.debug(...output);
}

function sanitizeContext(context: LogContext): LogContext {
  const result: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    result[key] = REDACTED_KEYS.test(key) ? '[redacted]' : sanitizeValue(value);
  }
  return result;
}

function sanitizeValue(value: unknown): unknown {
  if (value instanceof Error) return { name: value.name, message: value.message };
  if (typeof value === 'string') return value.length > 240 ? `${value.slice(0, 240)}…` : value;
  if (Array.isArray(value)) return `[array:${value.length}]`;
  if (value && typeof value === 'object') return '[object]';
  return value;
}
